import { Radio } from "antd";
import Search from "antd/es/input/Search";
import Loader from "../../components/loader/Loader";
import CouponProductItem from "./CouponProductItem";
import EmptyState from "../../components/emptyState/EmptyState";
import {getProductDiscounts} from "../../api/getProductDiscounts";
import {getShopProducts} from "../../api/getShopProducts";
import useUserContext from "../../hooks/useUserContext";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {setProductDiscount} from "../../api/setProductDiscount";
import {BurgerIcon} from "../../assets/icons/BurgerIcon";
import EnableAllCoupons from "./EnableAllCoupons";
import {shopifyIdNumber} from "../../utils/shopifyProductIdHandler";

interface CouponManager {
    isOpen: boolean;
}

const CouponManager = ({ isOpen }: CouponManager) => {
    const { selectedDomain } = useUserContext();
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState<'all' | 'selected' | 'non-selected'>('all');
    const [search, setSearch] = useState('');
    const [isActionBarOpen, setIsActionBarOpen] = useState(false);
    const [productsUpdatesCount, setProductsUpdateCount] = useState(0);
    const productsUpdatesCountRef = useRef(0);
    const productsMap = useRef(new Map());
    const variantsMap = useRef(new Map());
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);

    const getProducts = async () => {
        if(!selectedDomain) return;
        setIsLoading(true);
        const discounts = await getProductDiscounts(selectedDomain);
        const response = await getShopProducts(selectedDomain);
        response?.data?.response?.forEach((product: any) => {
            product.id = shopifyIdNumber(product.id);
            productsMap.current.set(product.id, product);
            if(product.variants?.length) {
                product.variants.forEach((variant: any) => {
                    variant.id = shopifyIdNumber(variant.id);
                    variant.product_id = shopifyIdNumber(variant.product_id);
                    variantsMap.current.set(`${product.id}-${variant.id}`, variant);
                });
            } else {
                variantsMap.current.set(`${product.id}-0`, product);
            }
        });
        discounts.data.forEach((discount: any) => {
            discount.product_id = shopifyIdNumber(discount.product_id);
            discount.variant_id = shopifyIdNumber(discount.variant_id);
            const previousVariant = variantsMap.current.get(`${discount.product_id}-${discount.variant_id}`);
            if(previousVariant?.id) {
                variantsMap.current.set(`${discount.product_id}-${discount.variant_id}`, {
                    ...previousVariant,
                    selected: true,
                    initialDiscount: discount.max_discount_percent
                });
            }
        });

        productsUpdatesCountRef.current++;
        setProductsUpdateCount(productsUpdatesCountRef.current);
        setIsLoading(false);
    }

    const onToggle = (product: any, variant: any, discountRate: number) => {
        if(isBulkUpdating) return;
        const previousVariant = variantsMap.current.get(`${product.id}-${variant.id}`);
        variantsMap.current.set(`${product.id}-${variant.id}`, { ...previousVariant, selected: !previousVariant.selected, initialDiscount: previousVariant.selected ? null : discountRate });
        productsUpdatesCountRef.current++;
        onDiscountRateUpdate(product, variant)(0);
        // setProductsUpdateCount(productsUpdatesCountRef.current);
    }

    const onDiscountRateUpdate = (product: any, variant: any) => (discountRate: number) => {
        if(isBulkUpdating) return;
        selectedDomain && setProductDiscount(selectedDomain, product.title, product.id, variant.id, discountRate);

        const previousVariant = variantsMap.current.get(`${product.id}-${variant.id}`);
        variantsMap.current.set(`${product.id}-${variant.id}`, { ...previousVariant, initialDiscount: discountRate });
        productsUpdatesCountRef.current++;
        setProductsUpdateCount(productsUpdatesCountRef.current);
    }

    const productsToShow = useMemo(() => {
        const pts: any[] = [];
        variantsMap.current.forEach((variant, key) => {
            const product = productsMap.current.get(variant.product_id);
            if((!search || product.title.toLowerCase().includes(search.toLowerCase()) || variant.title.toLowerCase().includes(search.toLowerCase())) && (filters === 'all' || (filters === 'selected' && variant.selected) || (filters === 'non-selected' && !variant.selected))) {
                pts.push({ ...product, variant });
            }
        });
        return pts;
    }, [productsUpdatesCount, search, filters]);

    const toggleActions = () => {
        setIsActionBarOpen(!isActionBarOpen);
    }

    const onBulkUpdateEnd = (discountValue: number, shouldOverwrite: boolean) => {
        variantsMap.current.forEach((variant, key) => {
            console.log("variant: ", variant.id);
            const shouldIgnore = variant.selected && !shouldOverwrite && discountValue;
            if (shouldIgnore) return;
            const product = productsMap.current.get(variant.product_id);
            variantsMap.current.set(`${product.id}-${variant.id}`, {
                ...variant,
                selected: !!discountValue,
                initialDiscount: discountValue
            });
        });
        productsUpdatesCountRef.current++;
        setProductsUpdateCount(productsUpdatesCountRef.current);
        setTimeout(() => {
            setIsBulkUpdating(false);
        }, 1000);
    }

    useEffect(() => {
        selectedDomain && isOpen && getProducts();
    }, [isOpen, selectedDomain]);

    return <div className="coupon-manager-container">
        <div className="products-search-bar">
            <div className={`more-actions ${isActionBarOpen ? 'selected' : ''}`} onClick={toggleActions}><BurgerIcon width={24} height={24} /></div>
            <div className="product-list-filters-container">
                <Radio.Group className='product-list-filters' value={filters} onChange={(e) => setFilters(e.target.value)}>
                    <Radio.Button value="all">All</Radio.Button>
                    <Radio.Button value="selected">Selected</Radio.Button>
                    <Radio.Button value="non-selected">Non-Selected</Radio.Button>
                </Radio.Group>
            </div>
            <Search className="search-field" type='search' placeholder="Product name" onChange={(event) => setSearch(event.target.value)} />
        </div>
        <div className={`coupons-action-bar-container ${isActionBarOpen ? 'shown' : ''}`}><div className="coupons-action-bar">
            <EnableAllCoupons onBulkUpdateStart={() => setIsBulkUpdating(true)} onBulkUpdateEnd={onBulkUpdateEnd} />
        </div></div>
        <div className='coupons-products-container'>
            {isLoading || isBulkUpdating ? <Loader /> : <div className="coupons-products">
                {/*{productsToShow?.length ? productsToShow.map(product => product.variants.map((variant: any) => <CouponProductItem key={`${product.id}-${variant.id}`} product={product} variant={variant} onToggle={onToggle} initialDiscountRate={variant.initialDiscount} onDiscountRateUpdate={onDiscountRateUpdate(product, variant)} />)) : <EmptyState subTitle={filters !== 'all' || search ? 'Try clearing your filters' : 'No products found'} />}*/}
                {productsToShow?.length ? productsToShow.map(product => {
                    if(!product?.id) return null;
                    return <CouponProductItem key={`${product.id}-${product.variant.id}`} product={product} variant={product.variant} onToggle={onToggle} initialDiscountRate={product.variant.initialDiscount} onDiscountRateUpdate={onDiscountRateUpdate(product, product.variant)} />
                }) : <EmptyState subTitle={filters !== 'all' || search ? 'Try clearing your filters' : 'No products found'} />}
            </div>}
        </div>
    </div>
}

export default CouponManager;